import { supabase } from '../lib/supabase';
import { AgeGroup } from '../types/models';

export const getAgeGroups = async (): Promise<AgeGroup[]> => {
  const { data, error } = await supabase
    .from('age_groups')
    .select('*')
    .order('birth_years', { ascending: false });
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const getAgeGroupById = async (id: string): Promise<AgeGroup | null> => {
  const { data, error } = await supabase
    .from('age_groups')
    .select('*')
    .eq('age_group_id', id)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const createAgeGroup = async (ageGroup: Omit<AgeGroup, 'age_group_id' | 'created_at'>): Promise<AgeGroup> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can create age groups');
  }

  const { data, error } = await supabase
    .from('age_groups')
    .insert([ageGroup])
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const updateAgeGroup = async (id: string, ageGroup: Partial<Omit<AgeGroup, 'age_group_id' | 'created_at'>>): Promise<AgeGroup> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update age groups');
  }

  const { data, error } = await supabase
    .from('age_groups')
    .update(ageGroup)
    .eq('age_group_id', id)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const deleteAgeGroup = async (id: string): Promise<void> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can delete age groups');
  }

  const { error } = await supabase
    .from('age_groups')
    .delete()
    .eq('age_group_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const checkUserCanManageAgeGroups = async (): Promise<boolean> => {
  const { data: userRoles } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  return userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  ) ?? false;
};